import { createMemo } from "solid-js";
import {
  buildTimeline,
  type CompletionMap,
  type CompletionRow,
  type CompletionStatus,
  computeStreaks,
  effectiveStatus,
  type HabitDefinitionTimeline,
  type HabitGoal,
  type HabitRow,
  type HabitVersionRow,
  inventoryCount,
  isScheduledOn,
  parseGoal,
  parseSchedule,
  protectedDaysByHabit,
  resolveDefinitionAt,
  type StreakProtectorRow,
  type StreakResult,
  toDayKey,
  weeklyProgress,
} from "@habits/shared";
import {
  useCompletionsQuery,
  useHabitsQuery,
  useHabitVersionsQuery,
  useStreakProtectorsQuery,
} from "@habits/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

export interface HabitWithDerived {
  habit: HabitRow;
  schedule: ReturnType<typeof parseSchedule>;
  /** Habit target (binary or quantified). */
  goal: HabitGoal;
  /** Map day → EFFECTIVE status ONLY for this habit (partials excluded). */
  completions: CompletionMap;
  streak: StreakResult;
  /** EFFECTIVE status for TODAY (null = unmarked or partial progress). */
  todayStatus: CompletionStatus | null;
  /** Numerical progress for TODAY (quantified habits; 0 if none). */
  todayAmount: number;
  /** Today's note (if any). */
  todayNote: string | null;
  /** Due today according to schedule? */
  scheduledToday: boolean;
  /** Weekly progress (only significant for x-per-week). */
  weekly: { count: number; target: number };
  /**
   * Definition timeline (interpretation sealing). Empty for
   * legacy habits / without history. Used by heatmap/stats to mark changes.
   */
  timeline: HabitDefinitionTimeline;
  /**
   * Days for THIS habit with a spent streak protector. Streak already
   * accounts for them; UI uses them to render differently from a broken gap.
   */
  protectedDays: ReadonlySet<string>;
}

interface RawCompletion {
  status: CompletionStatus;
  amount: number | null;
}

/**
 * Central domain hook: user habits + completions with all derived
 * state (streaks, today's status, weekly progress). Single subscription to
 * each table; views (Today, Habits, Calendar, Stats) memoize on top.
 *
 * `viewDayKey` parameterizes the "focused" day: `today*` fields (status, note,
 * progress) and streak are calculated relative to THAT day. Defaults to TODAY,
 * so existing views do not change; today-view day carousel uses it
 * to show previous/next day without touching the rest.
 */
const todayKey = () => toDayKey();

const EMPTY_PROTECTED_DAYS: ReadonlySet<string> = new Set<string>();

export function useHabitsData(
  opts: { includeArchived?: () => boolean; viewDayKey?: () => string } = {},
) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  const habits = useHabitsQuery({
    db,
    drizzle,
    userId,
    includeArchived: () => opts.includeArchived?.() ?? false,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const completions = useCompletionsQuery({
    db,
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  // Synchronized definition versions → timeline per habit for
  // HISTORICAL target resolution (interpretation sealing). A habit without
  // versions (legacy) always resolves to active target ⇒ current behavior.
  const versions = useHabitVersionsQuery({
    db,
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  // Streak protectors (inventory + spent). Local-first: rescuing a
  // missed day must work offline.
  const protectors = useStreakProtectorsQuery({
    db,
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const protectorRows = createMemo<StreakProtectorRow[]>(() => protectors.data ?? []);
  const protectedByHabit = createMemo(() => protectedDaysByHabit(protectorRows()));
  /** Available (unspent) protectors of user. */
  const availableProtectorCount = createMemo(() => inventoryCount(protectorRows()));

  const timelinesByHabit = createMemo(() => {
    const map = new Map<string, HabitVersionRow[]>();
    for (const row of versions.data ?? []) {
      const habitId = row.habit_id ?? "";
      const list = map.get(habitId);
      if (list) {
        list.push(row);
      } else {
        map.set(habitId, [row]);
      }
    }
    const timelines = new Map<string, HabitDefinitionTimeline>();
    for (const [habitId, rows] of map) {
      timelines.set(habitId, buildTimeline(rows));
    }
    return timelines;
  });

  const completionsByHabit = createMemo(() => {
    // Raw map (status + amount) per habit; EFFECTIVE status is derived
    // afterwards with each habit's target (parseGoal + effectiveStatus).
    const map = new Map<string, Map<string, RawCompletion>>();
    const notes = new Map<string, Map<string, string>>();
    for (const row of completions.data ?? []) {
      const habitId = row.habit_id ?? "";
      const date = row.date ?? "";
      const status: CompletionStatus = row.status === "skipped" ? "skipped" : "done";
      let inner = map.get(habitId);
      if (!inner) {
        inner = new Map();
        map.set(habitId, inner);
      }
      inner.set(date, { status, amount: row.amount ?? null });
      if (row.note) {
        let innerNotes = notes.get(habitId);
        if (!innerNotes) {
          innerNotes = new Map();
          notes.set(habitId, innerNotes);
        }
        innerNotes.set(date, row.note);
      }
    }
    return { map, notes };
  });

  // Focused day for `today*` fields + streak (defaults to today).
  const viewKey = () => opts.viewDayKey?.() ?? todayKey();

  const derived = createMemo<HabitWithDerived[]>(() => {
    const today = viewKey();
    const { map, notes } = completionsByHabit();
    const timelines = timelinesByHabit();
    const protectedDaysMap = protectedByHabit();
    return (habits.data ?? []).map((habit) => {
      const schedule = parseSchedule({
        schedule_kind: habit.schedule_kind,
        weekdays: habit.weekdays,
        times_per_week: habit.times_per_week,
      });
      const goal = parseGoal({
        goal_kind: habit.goal_kind,
        target_amount: habit.target_amount,
        target_unit: habit.target_unit,
      });
      const timeline = timelines.get(habit.id) ?? [];
      const raw = map.get(habit.id);
      // EFFECTIVE map: partial progress for a quantified habit does not count as done.
      // INTERPRETATION SEALING: each day is evaluated against the ACTIVE TARGET
      // on THAT day (resolved from version timeline), not against the current
      // target — thus raising target 10→20 does not re-judge past days.
      // Without versions (legacy) resolveDefinitionAt falls back to live target `goal`.
      const effective = new Map<string, CompletionStatus>();
      if (raw) {
        for (const [date, entry] of raw) {
          const { goal: goalAt } = resolveDefinitionAt(timeline, goal, schedule, date);
          const eff = effectiveStatus(goalAt, entry.status, entry.amount);
          if (eff !== null) {
            effective.set(date, eff);
          }
        }
      }
      const habitCompletions: CompletionMap = effective;
      const protectedDays: ReadonlySet<string> =
        protectedDaysMap.get(habit.id) ?? EMPTY_PROTECTED_DAYS;
      return {
        habit,
        schedule,
        goal,
        completions: habitCompletions,
        // A PAST, scheduled, unmarked day with spent protector does not break
        // streak (behaves like `skipped`). Without protectors calculation is
        // byte-for-byte historical behavior.
        streak: computeStreaks(schedule, habitCompletions, today, { protectedDays }),
        todayStatus: habitCompletions.get(today) ?? null,
        todayAmount: raw?.get(today)?.status === "skipped" ? 0 : (raw?.get(today)?.amount ?? 0),
        todayNote: notes.get(habit.id)?.get(today) ?? null,
        scheduledToday: isScheduledOn(schedule, today),
        weekly: weeklyProgress(habitCompletions, today, schedule.timesPerWeek),
        timeline,
        protectedDays,
      };
    });
  });

  return {
    habits,
    completions,
    versions,
    protectors,
    protectorRows,
    availableProtectorCount,
    derived,
    todayKey,
    /** Focused day for `today*` fields (today unless overridden). */
    viewKey,
    isLoading: () => habits.isLoading || completions.isLoading,
    userId,
  };
}

/**
 * Raw rows → map day→global status (global calendar).
 * `allowedHabitIds` filters orphaned completions of habits no longer
 * displayed (e.g. in trash): their check-ins are PRESERVED in DB but must
 * not paint the global calendar while habit is deleted.
 */
export function buildGlobalDayMap(
  rows: CompletionRow[] | undefined,
  allowedHabitIds?: Set<string>,
) {
  const byDay = new Map<string, { done: number; skipped: number }>();
  for (const row of rows ?? []) {
    if (allowedHabitIds && !allowedHabitIds.has(row.habit_id ?? "")) {
      continue;
    }
    const date = row.date ?? "";
    const entry = byDay.get(date) ?? { done: 0, skipped: 0 };
    if (row.status === "skipped") {
      entry.skipped += 1;
    } else {
      entry.done += 1;
    }
    byDay.set(date, entry);
  }
  return byDay;
}
